import { useEffect } from "react";

import { useRouter } from "next/router";

import { AssignmentsProvider } from "@/assignments/useAssignmentsGet";
import NewselaGlobalLayout from "@/base";
import RootProviders from "@/base/RootProviders";
import NotAllowedError from "@/errors/NotAllowedError";
import { NewSearchPageMVP } from "@/search/NewSearchPageMVP";
import { SearchPageProvider } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import { SearchPageProvider as LangstonSearchPageProvider } from "@/search/Langston/SearchPageProvider";
import { SearchPage as LangstonSearchPage } from "@/search/Langston/SearchPage";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import { ConceptTaxonomyProvider } from "@/search/Langston/ConceptTaxonomyProvider";

import { EXPERIMENT_TO_RETRIEVE_CONTENT_FROM_V2_LANGSTON_API } from "@/search/Langston/constants";
import type { NextPageContext } from "next";

const SearchPageRoute = () => {
  const { push, asPath: currentPath } = useRouter();

  const { data: user } = useUser();
  const {
    canViewSearch,
    isStudentInDistrictWithAllAdvisoryContent,
    isAuthenticatedUser,
  } = getUserProperties(user);

  useEffect(() => {
    if (!isAuthenticatedUser) {
      const signInUrl = "/signin";
      const encodedRedirectUrl = encodeURIComponent(currentPath);
      const signInAndRedirectUrl = `${signInUrl}?next=${encodedRedirectUrl}`;
      push(signInAndRedirectUrl);
    }
  }, [isAuthenticatedUser]);

  const [langstonSearchExperiment] = useGetUserExperimentSegment(
    EXPERIMENT_TO_RETRIEVE_CONTENT_FROM_V2_LANGSTON_API,
  );
  const isLangstonSearchOn = langstonSearchExperiment === "on";

  // If a user is a student in a Content Settings group that blocks search,
  // or is in an MCAT district, no searching allowed :(
  // only applies to logged in users
  if (
    isAuthenticatedUser &&
    (!canViewSearch || isStudentInDistrictWithAllAdvisoryContent)
  ) {
    return <NewselaGlobalLayout mainContents={<NotAllowedError />} />;
  }

  if (isLangstonSearchOn) {
    return (
      <NewselaGlobalLayout
        mainContents={
          <LangstonSearchPageProvider>
            <AssignmentsProvider>
              <ConceptTaxonomyProvider>
                <LangstonSearchPage />
              </ConceptTaxonomyProvider>
            </AssignmentsProvider>
          </LangstonSearchPageProvider>
        }
      />
    );
  }

  return (
    <NewselaGlobalLayout
      mainContents={
        <SearchPageProvider>
          <AssignmentsProvider>
            <NewSearchPageMVP />
          </AssignmentsProvider>
        </SearchPageProvider>
      }
    />
  );
};

export default SearchPageRoute;

export async function getServerSideProps(ctx: NextPageContext) {
  const RootProvidersInitialProps = await RootProviders.__getInitialProps({
    ctx,
  });

  return {
    props: {
      [RootProviders.InitialPropsName]: RootProvidersInitialProps,
    },
  };
}
